import { Injectable } from '@angular/core';

import { FeedbackModalComponent } from 'projects/tools/src/lib/shared-module/feedback-modal/feedback-modal.component';
import { ModalController, AnimationController } from '@ionic/angular/standalone';

import { PuzzleFrameworkService } from '../../puzzle-framework-module/puzzle-framework.service';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';

import { PuzzleFrameworkConfigModel } from 'projects/tools/src/lib/store/puzzle-framework-config.model';

import * as FeedbackActions from './store/feedback.actions';
import { FeedbackContextModel } from './store/feedback-context.model';
import { FeedbackEntryModel } from './store/feedback-entry.model';
import { FeedbackState } from './store/feedback.state';

import { buildEnv } from 'build-environment';

const __PROJECT_NAME__: string = buildEnv.projectName;

@Injectable({
  providedIn: 'root',
})
export class FeedbackService {
  public dialogIsOpen = false;

  private feedbackModal: HTMLIonModalElement;
  private feedbackModalIsActive = false;

  private puzzleFrameworkConfig: PuzzleFrameworkConfigModel;
  private selectedPuzzleName: string;

  constructor(
    private store: Store,
    private ionicModal: ModalController,
    private translate: TranslateService,
    private framework: PuzzleFrameworkService,
    public animationCtrl: AnimationController
  ) {}

  /*
   * `context` is optional and describes the situation the feedback was given in, i.e. the
   * puzzle id and the outcome of the round. It travels with the entry to Firestore and into
   * the notification mail, so feedback can be read without guessing what was played.
   */
  public async maybePromptFeedbackModal(featureId: string, context?: FeedbackContextModel) {
    if (!(this.framework.puzzleFrameworkConfig.feedbackFeatures || []).includes(featureId)) {
      return;
    }

    const feedbackEntry = await this.store
      .selectOnce(
        FeedbackState.getByID({ projectId: __PROJECT_NAME__, puzzleName: this.framework.configPuzzleName, featureId })
      )
      .toPromise();

    if (feedbackEntry?.score) {
      return;
    }

    await this.promptFeedbackModal(featureId, context);
  }

  public async promptFeedbackModal(featureId: string, context?: FeedbackContextModel) {
    if (this.feedbackModalIsActive) {
      return;
    }

    await this.store
      .selectOnce(
        FeedbackState.getByID({ projectId: __PROJECT_NAME__, puzzleName: this.framework.configPuzzleName, featureId })
      )
      .toPromise()
      .then(async (feedbackEntry: FeedbackEntryModel) => {
        if (!feedbackEntry || !feedbackEntry.score) {
          await this.store.dispatch(
            new FeedbackActions.InitFeedbackEntry({
              projectId: __PROJECT_NAME__,
              puzzleName: this.framework.configPuzzleName,
              featureId,
            })
          );
        }
      });

    if (context) {
      this.store.dispatch(
        new FeedbackActions.SetFeedbackContext({
          projectId: __PROJECT_NAME__,
          puzzleName: this.framework.configPuzzleName,
          featureId,
          context,
        })
      );
    }

    const animation = (baseEl: any, duration: number) => {
      const root = baseEl.shadowRoot;

      const backdropAnimation = this.animationCtrl
        .create()
        .addElement(root.querySelector('ion-backdrop'))
        .fromTo('opacity', '0.01', 'var(--backdrop-opacity)');

      const wrapperAnimation = this.animationCtrl
        .create()
        .addElement(root.querySelector('.modal-wrapper'))
        .keyframes([
          { offset: 0, opacity: '0', transform: 'scale(0)' },
          { offset: 1, opacity: '0.99', transform: 'scale(1)' },
        ]);

      return this.animationCtrl
        .create()
        .addElement(baseEl)
        .easing('ease-out')
        .duration(duration)
        .addAnimation([backdropAnimation, wrapperAnimation]);
    };

    const enterAnimation = (baseEl: any) => animation(baseEl, 1000);

    const leaveAnimation = (baseEl: any) => animation(baseEl, 500).direction('reverse');

    this.feedbackModalIsActive = true;
    this.dialogIsOpen = true;
    this.feedbackModal = await this.ionicModal.create({
      enterAnimation,
      leaveAnimation,
      component: FeedbackModalComponent,
      backdropDismiss: true,
      cssClass: ['puzzle-modal', 'puzzle-modal-smaller'],
      componentProps: {
        featureId,
      },
    });

    this.framework.pauseCurrentGame();
    await this.feedbackModal.present();

    const feedback = await this.feedbackModal.onDidDismiss();
    this.dialogIsOpen = false;

    this.framework.resumeCurrentGame();

    this.feedbackModalIsActive = false;
  }
}
